import React from 'react'
import { useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import type { SanityTextContentProps } from '@slite/components'
import { ArticleLayout, SanityTextRenderer } from '@slite/components'

import { NAVBAR_HEIGHT } from '../../../components/Navbar/NavbarSection'
import { Section } from '../../../components/Section'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useBanner } from '../../../components/useBanner'
import { useRoutes } from '../../../routes'
import { estimateReadingTimeMinutes } from '../../../utils/readingTime'
import { getSliteWebsiteConfig } from '../../data'
import type { Data } from './+data'

export default function LearnPage() {
  const { post, alternateHrefLangUrls } = useData<Data>()
  const intl = useIntl()
  const routes = useRoutes()
  const { navbarTopOffset } = useBanner()

  const pageUrl = routes.learn.detail({ slug: post.slug.current })
  const content = post.content as SanityTextContentProps
  const readingTime = estimateReadingTimeMinutes(content)

  return (
    <SlitePage
      title={post.title}
      ogImage={post.openGraphImage?.url || undefined}
      ogTitle={post.openGraphTitle ?? post.title}
      description={post.metaDescription ?? undefined}
      canonicalUrl={pageUrl}
      alternateHrefLangUrls={alternateHrefLangUrls}
      background="moon-dust-lighter"
      additionalMetaTags={[
        { name: 'og:type', content: 'article' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@slitehq' },
        { name: 'twitter:creator', content: '@slitehq' },
      ]}
    >
      <Section background="moon-dust-lighter" containerTopPadding={40}>
        <ArticleLayout
          title={post.title}
          description={post.openGraphDescription ?? undefined}
          date={intl.formatDate(post.date, { dateStyle: 'medium' })}
          dateTime={post.date}
          readingTime={intl.formatMessage(
            {
              id: 'learn.show.metaReadingTime',
              defaultMessage: '{readingTime} minutes read',
            },
            { readingTime }
          )}
          category={intl.formatMessage({
            id: 'learn.show.category',
            defaultMessage: 'Learn article',
          })}
          authors={[
            {
              name: post.author.name,
              imageUrl: post.author.picture.url,
              bio: post.author.bio as SanityTextContentProps,
            },
          ]}
          authorsLabel={intl.formatMessage({
            id: 'learn.show.writtenBy',
            defaultMessage: 'Written by',
          })}
          backLabel={intl.formatMessage({
            id: 'learn.show.allArticles',
            defaultMessage: 'All articles',
          })}
          backHref={routes.learn.index()}
          ctaLabel={
            post.ctaLabel ||
            intl.formatMessage({
              id: 'learn.show.cta',
              defaultMessage: 'Sign up',
            })
          }
          ctaHref={post.ctaLink || routes.auth.signup()}
          showCtaInSidebar
          shareUrl={`${getSliteWebsiteConfig(intl).domain}${pageUrl}`}
          tocLabel={intl.formatMessage({
            id: 'learn.show.toc.title',
            defaultMessage: 'Table of contents',
          })}
          shareLabel={intl.formatMessage({
            id: 'learn.show.share',
            defaultMessage: 'Share',
          })}
          shareOnXLabel={intl.formatMessage({
            id: 'learn.show.shareOnX',
            defaultMessage: 'Share on X',
          })}
          shareOnLinkedInLabel={intl.formatMessage({
            id: 'learn.show.shareOnLinkedIn',
            defaultMessage: 'Share on LinkedIn',
          })}
          stickyTopOffset={NAVBAR_HEIGHT + navbarTopOffset}
        >
          <SanityTextRenderer variant="website" content={content} />
        </ArticleLayout>
      </Section>
      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
